import { flatten, union } from 'lodash-es';
import { apiWrapper } from 'utils/reduxUtils';
import { getAllApi } from 'api/crud';
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { PREFIX_API_VER_2 } from 'configs/localData/constant';
import {
  convertResponseData,
  convertRequestParams,
  PRIMARY_KEY,
} from '../crudCreator/dataProvider';

const debouncedIds = {};
const mappeds = {};
const tasks = {};

const addIds = (resource, ids) => {
  if (!debouncedIds[resource]) {
    debouncedIds[resource] = [];
  }
  debouncedIds[resource] = flatten(union(debouncedIds[resource], ids));
};

const addMappedBy = (resource, filterKey) => {
  if (!mappeds[resource]) {
    mappeds[resource] = [];
  }
  mappeds[resource] = filterKey;
};

function finalize(resource, options, dispatch) {
  dispatch(retrieveReferenceList({ resource, options }));
  delete tasks[resource];
  delete debouncedIds[resource];
  delete mappeds[resource];
}

export const retrieveReferenceInputData = createAsyncThunk(
  'reference/retrieveReferenceInputData',
  async ({ resource, data, options = {} }, thunkAPI) => {
    try {
      const { filter, outsideFilter, limit, offset } =
        thunkAPI.getState().reference?.[resource];
      const {
        filter: filterData,
        outsideFilter: outsideFilterData,
        ...restData
      } = data;
      const params = {
        limit: limit || 10,
        offset: offset || 0,
        ...outsideFilter,
        ...restData,
        ...outsideFilterData,
        filter: {
          ...filter,
          ...filterData,
        },
      };
      const convertRequest = convertRequestParams('GET_ALL', params);
      const response = await apiWrapper(
        { isShowProgress: false },
        getAllApi,
        options.customApiResource || resource,
        convertRequest,
        options.prefixUrl,
        options?.requestParams,
        options?.requestApi,
      );
      const result = convertResponseData('GET_ALL', response, options);
      return {
        resource,
        referenceData: {
          ...result,
          numberOfPages: Math.ceil(result.total / (limit || 10)),
        },
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({ resource, error });
    }
  },
);

export const retrieveReferenceList = createAsyncThunk(
  'reference/retrieveReferenceList',
  async ({ resource, options = {} }, thunkAPI) => {
    try {
      const params = {
        limit: 100,
        offset: 0,
        filter: JSON.stringify({
          [mappeds[resource] || PRIMARY_KEY]: {
            $in: debouncedIds[resource],
          },
        }),
      };
      const response = await apiWrapper(
        { isShowProgress: false },
        getAllApi,
        options.customApiResource || resource,
        params,
      );

      const result = convertResponseData('GET_ALL', response, options);
      return { resource, data: result };
    } catch (error) {
      return thunkAPI.rejectWithValue({ resource, error });
    }
  },
);

export const retrieveReference = createAsyncThunk(
  'reference/retrieveReference',
  async (
    { resource, ids, mappedBy, filterKey, customApiResource },
    thunkAPI,
  ) => {
    try {
      if (tasks[resource]) {
        clearTimeout(tasks[resource]);
        tasks[resource] = null;
      }

      addIds(resource, ids);
      addMappedBy(resource, filterKey);

      tasks[resource] = setTimeout(() => {
        finalize(
          resource,
          { primaryKey: mappedBy, customApiResource },
          thunkAPI.dispatch,
        );
      }, 50);
    } catch (error) {
      return thunkAPI.rejectWithValue({ resource, error });
    }
    return undefined;
  },
);

export const clearData = createAction('clearData');

export const retrieveReferenceInputDataVer2 = createAsyncThunk(
  'reference/retrieveReferenceInputData',
  async ({ resource, data, options = {} }, thunkAPI) => {
    try {
      const { filter, outsideFilter } =
        thunkAPI.getState().reference?.[resource];

      const {
        filter: filterData,
        outsideFilter: outsideFilterData,
        ...restData
      } = data;
      const params = {
        size: data?.size || 10,
        page: data?.page || 1,
        ...outsideFilter,
        ...restData,
        ...outsideFilterData,
        filter: {
          ...filter,
          ...filterData,
        },
      };
      const convertRequest = convertRequestParams(
        'GET_ALL',
        params,
        {},
        { prefixApi: PREFIX_API_VER_2 },
      );
      const response = await apiWrapper(
        { isShowProgress: false },
        getAllApi,
        options.customApiResource || resource,
        convertRequest,
        options.prefixUrl,
        options?.requestParams,
        options?.requestApi,
      );
      const result = convertResponseData('GET_ALL', response, options);
      return {
        resource,
        referenceData: {
          ...result,
          page: Math.ceil(
            result.additionData.paging.total / (data?.size || 10),
          ),
          total: data?.total,
        },
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({ resource, error });
    }
  },
);
